interface ErrorWithMessage {
	message: string
}

/**
 * Type guard that checks if an unknown value has a string message property.
 * @param error The value to check.
 * @returns true if the value has a message property of type string.
 */
function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
	return typeof error === "object" && error !== null && "message" in error && typeof (error as Record<string, unknown>)["message"] === "string";
}

function isError(error: unknown): error is Error {
	if ("isError" in Error && typeof Error.isError === "function") {
		return Error.isError(error);
	}

	return error instanceof Error;
}

/**
 * Converts any value to an Error instance.
 * If the value is already an Error, returns it unchanged.
 * Otherwise, attempts to extract or create an appropriate error message.
 * @param maybeError The value to convert to an Error.
 * @returns An Error instance.
 */
export default function ensureErrorType(maybeError: unknown): Error {

	if (isError(maybeError)) {
		return maybeError;
	}

	if (maybeError instanceof ErrorEvent) {
		//cross-origin script errors carry a null .error — never return it typed as Error
		return maybeError.error != null ? ensureErrorType(maybeError.error) : new Error(maybeError.message || "Unknown script error");
	}

	if (isErrorWithMessage(maybeError)) {
		return new Error(maybeError.message);
	}

	try {
		return new Error(JSON.stringify(maybeError))
	} catch {
		return new Error(String(maybeError))
	}
}
